import { FIELD_NAME_LABELS } from './fieldLabels';
import type { DeliveryStatus, ReceiptOutcome, ReferenceKind, ResolutionMethod } from './schema';

/**
 * Spanish text for the closed vocabularies `backend/app/validation/findings.py` and
 * `backend/app/reviews/detail.py` produce. `backend/app/validation/findings.py` states the split
 * outright: "The Spanish the owner reads is the web app's to write" - this module is that.
 *
 * Every lookup falls back to the raw code rather than throwing, so a code this build does not
 * yet know about still reaches the owner as *something* instead of crashing the review screen -
 * `docs/sales_note_requirements.md` §30's "make exceptions fast to find" fails if an unfamiliar
 * finding hides the screen instead of showing up oddly.
 */

const FINDING_LABELS: Readonly<Record<string, string>> = {
  line_amount_disagrees: 'La cantidad por el precio no da el importe escrito.',
  line_amount_rounded: 'Coincide solo redondeando a centavos.',
  line_amount_uncheckable: 'Falta un dato de esta fila para comprobarla.',
  note_total_disagrees: 'La suma de las filas no da el total escrito.',
  note_total_rounded: 'La suma coincide con el total solo redondeando a centavos.',
  note_total_uncheckable: 'Falta un importe para poder sumar el total.',
  no_rows: 'No se reconoció ninguna fila en esta nota.',
  empty_row: 'Esta fila llegó sin ningún dato.',
  row_order_broken: 'El orden de las filas no coincide con lo registrado.',
  field_not_reported: 'El reconocimiento no dijo nada sobre este dato.',
  field_blank: 'Se leyó que este dato está en blanco en el papel.',
  field_illegible: 'No se pudo leer este dato con claridad.',
  duplicate_looking_row: 'Esta fila se parece mucho a otra de la nota.',
  possible_missing_row:
    'El total escrito es mayor que la suma de las filas leídas; podría faltar una fila.',
  possible_extra_row:
    'Las filas leídas superan el total por el importe de una de ellas; podría haber una fila de más.',
  non_positive_quantity: 'La cantidad no puede ser cero o negativa.',
  negative_value: 'Este valor no puede ser negativo.',
  value_out_of_range: 'Este número parece fuera de lo normal para esta nota.',
  date_unparsed: 'No se pudo interpretar esta fecha.',
  date_ambiguous: 'Esta fecha se puede leer de más de una forma.',
  date_in_future: 'Esta fecha es posterior a hoy.',
  date_too_old: 'Esta fecha es muy anterior a hoy.',
};

/** Spanish text for one validation-flag code, or the code itself when unrecognized. */
export function findingLabel(code: string): string {
  return FINDING_LABELS[code] ?? code;
}

const CONFIDENCE_BAND_LABELS: Readonly<Record<string, string>> = {
  high: 'Confianza alta',
  medium: 'Confianza media',
  low: 'Confianza baja',
};

/** Spanish text for a prediction's confidence band, or the raw value when unrecognized. `null`
 * (no band recorded) is the caller's own concern - most callers show nothing at all then. */
export function confidenceBandLabel(band: string): string {
  return CONFIDENCE_BAND_LABELS[band] ?? band;
}

const RESOLUTION_METHOD_LABELS: Readonly<Record<ResolutionMethod, string>> = {
  exact: 'Coincidencia exacta con Excel',
  alias: 'Resuelto por un alias confirmado',
  fuzzy: 'Resuelto por parecido de texto',
  context: 'Resuelto por el contexto de la nota',
  model: 'Resuelto por el modelo de reconocimiento',
  unresolved: 'Sin resolver',
};

/** Spanish text for how Stage B arrived at (or failed to arrive at) an interpreted value. */
export function resolutionMethodLabel(method: ResolutionMethod): string {
  return RESOLUTION_METHOD_LABELS[method];
}

/**
 * Spanish text for `alert.message_key`, the fourth closed vocabulary this module covers
 * (`backend/app/api/lists.py`'s module docstring names all four the alert list can carry):
 * the reference-issue key from `app/references/repositories.py`, the `validation.{code}` keys
 * `app/notes/reconciliation.py::validation_message_key` builds from `FindingCode` (reusing
 * `FINDING_LABELS` above rather than duplicating that vocabulary), the two fixed keys from
 * `app/reviews/service.py`/`app/agents/deliveries.py`, and `recognition.standby_provider` from
 * `app/runs/service.py` - raised on a note the backup recognizer read because the selected one
 * was saturated, so the owner knows which readings came from a model nobody has scored. Falls
 * back to the raw key for anything this build does not yet know, same as `findingLabel`.
 */
const ALERT_MESSAGE_LABELS: Readonly<Record<string, string>> = {
  'reference_issue.open': 'Cliente o concepto sin resolver en Excel.',
  'recognition.failed':
    'No se pudo reconocer esta nota. Sigue visible; puedes intentar el reconocimiento de nuevo.',
  'recognition.standby_provider':
    'El servicio principal estaba saturado, así que esta nota la leyó el servicio de respaldo. Revísala con más cuidado de lo habitual.',
  'review.post_export_edit':
    'Esta nota ya fue enviada a Excel; revisa la corrección también en Excel.',
  'delivery.attention':
    'El envío a Excel necesita tu atención; revisa si la nota ya quedó registrada antes de reintentar.',
  'delivery.release.excel_unavailable':
    'Excel no está disponible en la PC del agente. El envío se reintentará cuando vuelva a estar disponible.',
  'delivery.release.workbook_in_use':
    'El libro de Excel está en uso. Cierra el libro en la PC del agente para reintentar el envío.',
  'delivery.release.form_not_empty':
    'El formulario Nota Venta de Excel no está vacío. Revísalo en la PC del agente antes de reintentar el envío.',
};

/** Spanish text for `excel_delivery.status` (`backend/app/domain/enums.py`'s `DeliveryStatus`),
 * distinct from a note's own nine-state lifecycle (`notes/status.ts`) - this describes the
 * queued delivery row itself, shown alongside the note status on the review screen's
 * "delivered evidence" summary (6.6). */
const DELIVERY_STATUS_LABELS: Readonly<Record<DeliveryStatus, string>> = {
  pending: 'Pendiente de que el agente de Excel la tome',
  leased: 'El agente de Excel la está trabajando',
  delivered: 'Entregada y verificada en Excel',
  attention: 'Necesita que revises Excel',
};

export function deliveryStatusLabel(status: DeliveryStatus): string {
  return DELIVERY_STATUS_LABELS[status];
}

/** Spanish text for `agent_receipt.outcome` (`backend/app/domain/enums.py`'s `ReceiptOutcome`) -
 * the only two durable conclusions the Windows agent may report about one macro attempt. */
const RECEIPT_OUTCOME_LABELS: Readonly<Record<ReceiptOutcome, string>> = {
  verified: 'Verificada: las filas aparecieron en Excel',
  ambiguous: 'Ambigua: no se pudo comprobar si Excel quedó actualizado',
};

export function receiptOutcomeLabel(outcome: ReceiptOutcome): string {
  return RECEIPT_OUTCOME_LABELS[outcome];
}

const VALIDATION_MESSAGE_KEY_PREFIX = 'validation.';

const REFERENCE_ISSUE_OPEN_KEY = 'reference_issue.open';

/** The customer or concept one `reference_issue.open` alert is about, when the alert carries it
 * (`AlertResponse.reference_kind`/`reference_observed_text`, `backend/app/api/lists.py` and
 * `backend/app/api/review.py`). Passed to `alertMessageLabel` so two open alerts on the same
 * note - one per unresolved row - read as two different things instead of the same generic
 * sentence repeated. */
export interface ReferenceAlertContext {
  readonly kind: ReferenceKind | null;
  readonly observedText: string | null;
}

export function alertMessageLabel(messageKey: string, reference?: ReferenceAlertContext): string {
  if (
    messageKey === REFERENCE_ISSUE_OPEN_KEY &&
    reference?.kind != null &&
    reference.observedText !== null &&
    reference.observedText !== ''
  ) {
    return `${FIELD_NAME_LABELS[reference.kind]} «${reference.observedText}» sin resolver en Excel.`;
  }
  const direct = ALERT_MESSAGE_LABELS[messageKey];
  if (direct !== undefined) {
    return direct;
  }
  if (messageKey.startsWith(VALIDATION_MESSAGE_KEY_PREFIX)) {
    return findingLabel(messageKey.slice(VALIDATION_MESSAGE_KEY_PREFIX.length));
  }
  return messageKey;
}
